import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountSelectionService, AsinService } from "@front/m19-services";
import {
  IButtonDirective,
  IIconComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { ManagedCatalogPageService } from "@m19-board/services/managed-catalog-page.service";
import { UnmanagedCatalogPageService } from "@m19-board/services/unmanaged-catalog-page.service";

@Component({
  selector: "app-import-managed-catalog-modal",
  template: ` <div class="m-4">
    <textarea
      type="text"
      #asins
      class="textarea-width form-control coding-font"
      placeholder="asin1&#10;asin2"
      [value]="asinsInput()"
      (input)="asinsInput.set(asins.value)"
    ></textarea>
    <div class="mt-2 flex items-center justify-end">
      <button iButton size="md" [disabled]="isUploadButtonDisabled()" (click)="import()">
        {{ modalData.mode === "managed" ? "Restore To Managed Catalog" : "Stop Managing Products" }}
      </button>
      <button iButton variant="secondary" size="md" class="ml-2" (click)="modalRef.close()">Close</button>
    </div>
    <div class="mt-2">
      @if (errors() && errors().length > 0) {
        <ul class="list-group">
          <li class="list-group-item list-group-item list-group-item-warning flex justify-between">
            <span>The following ASINs have been ignored:</span>

            <button iButton size="icon-sm" variant="ghost" [iTooltip]="'Close error table'" (click)="errors.set([])">
              <IIcon name="close" />
            </button>
          </li>
          @for (error of errors(); track error) {
            <li class="list-group-item list-group-item-secondary py-0">
              {{ error }}
            </li>
          }
        </ul>
      }
    </div>
  </div>`,
  styles: [
    `
      textarea {
        resize: none;
        height: 250px;
        border-bottom-right-radius: 0%;
      }
    `,
  ],
  imports: [IButtonDirective, ITooltipDirective, IIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportManagedCatalogModalComponent {
  private readonly asinService = inject(AsinService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly managedCatalogPageService = inject(ManagedCatalogPageService);
  private readonly unmanagedCatalogPageService = inject(UnmanagedCatalogPageService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  modalData = inject(MODAL_DATA) as ImportManagedCatalogModalData;
  modalRef = inject(ModalRef);

  private readonly managedCatalog = computed(
    () => this.managedCatalogPageService.managedCatalog()?.map((p) => p.asin!) ?? [],
  );
  private readonly unmanagedCatalog = computed(
    () => this.unmanagedCatalogPageService.unmanagedCatalog()?.map((p) => p.asin!) ?? [],
  );
  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  errors = signal<string[]>([]);
  protected readonly asinsInput = signal("");
  protected readonly isUploadButtonDisabled = computed(() => this.asinsInput().trim().length === 0);

  import(): void {
    const inputAsins = this.asinsInput();
    this.asinsInput.set("");
    const asins: string[] = [];

    const errors: string[] = [];
    for (const asin of inputAsins.split(/[\s,;\n]+/)) {
      const normalizedAsin = Utils.normalizeASIN(asin);
      if (normalizedAsin === "") {
        errors.push(asin + ": " + this.translocoService.translate("seeds-edition-modal.invalid_asin"));
        continue;
      }
      if (asins.includes(normalizedAsin)) {
        errors.push(asin + ": " + this.translocoService.translate("import-managed-catalog-modal.duplicated"));
        continue;
      }
      if (this.modalData.mode === "managed" && this.managedCatalog().includes(normalizedAsin)) {
        errors.push(
          asin + ": " + this.translocoService.translate("import-managed-catalog-modal.already_in_managed_catalog"),
        );
        continue;
      }
      if (this.modalData.mode === "managed" && !this.unmanagedCatalog().includes(normalizedAsin)) {
        errors.push(asin + ": " + this.translocoService.translate("import-managed-catalog-modal.asin_is_unknown"));
        continue;
      }
      if (this.modalData.mode === "unmanaged" && this.unmanagedCatalog().includes(normalizedAsin)) {
        errors.push(
          asin + ": " + this.translocoService.translate("import-managed-catalog-modal.already_in_unmanaged_catalog"),
        );
        continue;
      }
      if (this.modalData.mode === "unmanaged" && !this.managedCatalog().includes(normalizedAsin)) {
        errors.push(asin + ": " + this.translocoService.translate("import-managed-catalog-modal.asin_is_unknown"));
        continue;
      }

      asins.push(normalizedAsin);
    }
    this.errors.set([...errors]);

    if (asins.length == 0) {
      errors.push(this.translocoService.translate("import-managed-catalog-modal.no_asins_to_add_to_catalog"));
      this.errors.set([...errors]);
      return;
    }

    if (this.modalData.mode === "managed") {
      this.asinService
        .addAsins(this.accountMarketplace()!.accountId, this.accountMarketplace()!.marketplace, asins)
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translocoService.translate("catalog-page.products_are_back_to_managed_catalog"),
            );
          },
          error: (err) => {
            this.toastrService.error(
              err,
              this.translocoService.translate("catalog-page.failed_to_move_back_products_to_managed_catalog"),
            );
          },
        });
    } else {
      this.asinService
        .deleteAsins(this.accountMarketplace()!.accountId, this.accountMarketplace()!.marketplace, asins)
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translocoService.translate("import-managed-catalog-modal.products_move_to_unmanaged_catalog"),
            );
          },
          error: (err) => {
            this.toastrService.error(
              err,
              this.translocoService.translate(
                "import-managed-catalog-modal.failed_to_move_products_to_unmanaged_catalog",
              ),
            );
          },
        });
    }
  }
}

export interface ImportManagedCatalogModalData {
  mode: ImportManagedCatalogMode;
}

export enum ImportManagedCatalogMode {
  MANAGED = "managed",
  UNMANAGED = "unmanaged",
}
